import React, { useEffect } from 'react'
import { Language, languages } from "../locale"
import { app } from "../app"
import { useNavigate, useParams } from 'react-router-dom'
import { buildLocalePath, getLocaleByZendesk, getSupportedLocales } from '../helpers'
import { DownOutlined, LanguageIcon } from './icons'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"

interface LanguageDropdownProps {
  isMobile?: boolean
}

export function LanguageDropdown({ isMobile = false }: LanguageDropdownProps) {
  const navigate = useNavigate()
  const { locale } = useParams()

  const supportedLocales = React.useMemo(() => {
    return getSupportedLocales().filter(locale => locale !== app.locale)
  }, [app.page.content, app.defaultLocale, app.locale])

  const handleLanguageChange = (lang: string) => {
    if (app.locale === lang) return

    app.locale = lang
    localStorage.setItem('preferredLanguage', lang)
    navigate(buildLocalePath(lang, location.pathname), { replace: true })
    app.update()
  }


  const normalizeLanguageKeys = (languages: Record<string, Language>) => {
    return Object.keys(languages).reduce<Record<string, Language>>((acc, key) => {
      acc[key.toLowerCase()] = languages[key]
      return acc
    }, {})
  }

  const normalizedLanguages = normalizeLanguageKeys(languages)

  const getLanguageName = (localeCode: keyof typeof languages) => {
    return normalizedLanguages[localeCode.toLowerCase()]?.name || localeCode
  }

  useEffect(() => {
    if (!locale) return
    app.locale = getLocaleByZendesk(locale)
  }, [locale])

  const isRTL = languages[app.locale]?.rtl

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className={`language-dropdown`} role="button" aria-label={getLanguageName(app.locale as keyof typeof languages)}>
          <div className='flex items-center gap-1'>
            <LanguageIcon aria-hidden="true" style={{ fontSize: isMobile ? '1.25rem' : '1.125rem' }} />
            <span className="language-label" style={{ fontWeight: 500, fontSize: isMobile ? "12px" : "16px" }}>{getLanguageName(app.locale as keyof typeof languages)}</span>
            <DownOutlined aria-hidden="true" className={`dropdown-icon ${isRTL ? 'mr-1' : 'ml-1'}`} style={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }} />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end">
        <DropdownMenuGroup>
          {supportedLocales.map((localeCode) => {
            const languageName = getLanguageName(localeCode.toLowerCase() as keyof typeof languages)
            return (
              <DropdownMenuItem
                key={localeCode}
                onClick={() => handleLanguageChange(localeCode)}
              >
                {languageName}
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
